"use client";

import React from "react";
import { Boxes, Network, Zap, FileText, Headset, TrendingUp, ArrowRight, CircleX, CircleCheck } from "lucide-react";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const differentiators = [{
  id: "omnichannel",
  title: "Gestão de Estoque",
  pain: "Estoque descentralizado, furos de venda e dificuldade em unificar canais físicos e digitais.",
  solution: "Visão omnichannel em tempo real com OMS nativo, transformando lojas em mini-hubs de distribuição.",
  icon: Boxes
}, {
  id: "integration",
  title: "Conectividade",
  pain: "Integrações complexas, instáveis e que exigem alto custo de desenvolvimento e manutenção.",
  solution: "Ecossistema plug-and-play com mais de 200 integrações nativas com marketplaces, hubs e ERPs.",
  icon: Network
}, {
  id: "performance",
  title: "Processamento",
  pain: "Lentidão no faturamento e processamento de pedidos durante picos de acesso.",
  solution: "Motor de regras de negócios de altíssima performance, preparado para grandes volumes.",
  icon: Zap
}, {
  id: "fiscal",
  title: "Compliance Fiscal",
  pain: "Dificuldade na gestão tributária, multas por erros e lentidão na emissão de notas.",
  solution: "Automação fiscal completa, atualizada com a legislação brasileira e emissão em lote.",
  icon: FileText
}, {
  id: "support",
  title: "Acompanhamento",
  pain: "Suporte genérico, demorado e falta de direcionamento estratégico para o negócio.",
  solution: "Consultoria especialista LinxConsult, implantação guiada e suporte técnico próximo e ágil.",
  icon: Headset
}, {
  id: "scalability",
  title: "Escalabilidade",
  pain: "Sistemas que travam na Black Friday e limitam o crescimento da operação.",
  solution: "Arquitetura em nuvem robusta, elástica e segura, que cresce junto com o seu faturamento.",
  icon: TrendingUp
}];
export default function CompetitiveDifferentiation() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section id="competitivedifferentiation" className="py-24 bg-background relative overflow-hidden">
      {}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full max-w-4xl h-96 bg-primary/5 blur-[120px] rounded-full pointer-events-none" />
      <div className="absolute bottom-0 right-0 w-96 h-96 bg-primary/10 blur-[150px] rounded-full pointer-events-none" />

      <div className="container mx-auto px-4 relative z-10">
        <div ref={ref} className={`text-center max-w-3xl mx-auto mb-16 transition-all duration-1000 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <Badge variant="outline" className="mb-4 border-primary/30 text-primary bg-primary/5">
            Por que escolher o e-Millennium?
          </Badge>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-foreground mb-6 tracking-tight">
            Diferenciais que Impulsionam seu Negócio
          </h2>
          <p className="text-lg text-muted-foreground">
            Descubra como a tecnologia robusta do e-Millennium, aliada à expertise da LinxConsult, 
            supera os principais gargalos das operações de atacado, indústria e varejo omnichannel.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
          {differentiators.map((item, i) => {
          const Icon = item.icon;
          return <Card key={item.id} data-index={i} className={`group relative overflow-hidden border-border/40 bg-card/40 backdrop-blur-xl hover:border-primary/50 transition-all duration-500 hover:shadow-[0_0_30px_-10px_rgba(255,0,102,0.15)] ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`} style={{
            transitionDelay: `${i * 100}ms`
          }}>
                {}
                <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-primary/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                
                <CardHeader className="pb-4">
                  <div className="w-14 h-14 rounded-xl bg-background/80 border border-border/50 flex items-center justify-center mb-4 group-hover:scale-110 group-hover:bg-primary/10 group-hover:border-primary/30 transition-all duration-500 shadow-sm">
                    <Icon className="w-7 h-7 text-primary" strokeWidth={1.5} />
                  </div>
                  <CardTitle className="text-xl font-semibold text-foreground">
                    {item.title}
                  </CardTitle>
                </CardHeader>
                
                <CardContent className="space-y-5">
                  <div className="space-y-2.5">
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <CircleX className="w-4 h-4 text-destructive/70" />
                      <span className="text-xs font-medium uppercase tracking-wider text-destructive/90">O Desafio</span>
                    </div>
                    <p className="text-sm text-muted-foreground leading-relaxed">
                      {item.pain}
                    </p>
                  </div>
                  
                  <Separator className="bg-border/40" />
                  
                  <div className="space-y-2.5">
                    <div className="flex items-center gap-2 text-primary">
                      <CircleCheck className="w-4 h-4" />
                      <span className="text-xs font-medium uppercase tracking-wider">A Solução Linx</span>
                    </div>
                    <p className="text-sm font-medium text-foreground leading-relaxed">
                      {item.solution}
                    </p>
                  </div>
                </CardContent>
              </Card>;
        })}
        </div>

        <div className={`mt-16 text-center transition-all duration-1000 delay-700 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <Button asChild size="lg" className="rounded-full px-8 h-12 text-base shadow-lg shadow-primary/20 hover:shadow-primary/40 transition-all duration-300">
            <Link to="/ContactsPage">
              Solicitar Demonstração Técnica
              <ArrowRight className="ml-2 w-5 h-5" />
            </Link>
          </Button>
          <p className="mt-4 text-sm text-muted-foreground">
            Fale com um especialista da LinxConsult e descubra o potencial do seu negócio.
          </p>
        </div>
      </div>
    </section>;
}